export const profile = {
  name: "Ahmed Saleem",
  title: "Senior Golang Backend Engineer",
  roles: ["Golang Backend Engineer", "Backend Architecture", "DevOps", "Real-time Systems", "Microservices"],
  email: "ahmedsaleem3942@gmail.com",
  phone: "+92 331 8360836",
  location: "Haripur, Pakistan · Open to remote",
  github: "https://github.com/AhmedSaleemKhan",
  githubUser: "AhmedSaleemKhan",
  linkedin: "https://www.linkedin.com/in/ahmed-saleem-khan",
  summary:
    "Senior backend engineer with 6+ years building high-throughput services in Go, from first architecture sketch to production servers. Currently owns the backend and DevOps for NimTalk, a real-time voice and text chat platform, after leading the migration of its Laravel monolith to Go. Previously architected a high-volume SMS/MMS marketing platform for a US client on gRPC microservices, Kafka, RabbitMQ and Twilio.",
  about: [
    "Strong across API design, relational and NoSQL data modelling, event-driven messaging, Docker, CI/CD and Linux server hardening.",
    "I have led full-stack teams, mentored junior developers, and teach university computing labs.",
  ],
};

export const stats = [
  { value: 6, suffix: "+", label: "Years in backend" },
  { value: 44, suffix: "+", label: "Projects delivered" },
  { value: 3, suffix: "", label: "Companies · US & PK" },
  { value: 30, suffix: "+", label: "Technologies used" },
];

export const skills = [
  { group: "Languages", items: ["Go (Golang)", "SQL", "Bash", "JavaScript / TypeScript", "Solidity"] },
  { group: "Go ecosystem", items: ["Gin", "Echo", "Gorilla/mux", "GORM", "sqlx", "sqlc", "gRPC / Protobuf", "JWT"] },
  { group: "APIs & real-time", items: ["REST", "gRPC", "GraphQL", "SOAP", "WebSockets", "Socket.IO"] },
  { group: "Data & caching", items: ["PostgreSQL", "MySQL", "MongoDB", "SQLite", "Redis"] },
  { group: "Messaging", items: ["Kafka", "RabbitMQ", "Event-driven microservices", "Cron workers"] },
  { group: "Cloud", items: ["AWS (Lambda, S3, RDS, Cognito)", "GCP", "DigitalOcean", "Hetzner", "Linode", "Namecheap"] },
  { group: "DevOps", items: ["Docker", "Docker Compose", "Kubernetes", "CI/CD", "Makefiles", "systemd", "Linux admin", "SSH hardening"] },
  { group: "Integrations", items: ["Twilio (SMS/MMS)", "Cloudflare Turnstile", "Jibble API", "FFmpeg"] },
  { group: "Frontend & Web3", items: ["React.js", "Vite", "ethers.js", "MetaMask", "Avalanche", "ERC-20"] },
  { group: "Practices", items: ["Clean / layered architecture", "Code review", "Mentoring", "Git / GitHub", "Postman", "Jira"] },
];

export const experience = [
  {
    role: "Senior Backend Golang Developer & DevOps",
    company: "NimTalk LLC",
    place: "California, USA",
    period: "Remote · Present",
    intro: "Own the backend, databases, APIs and production infrastructure of a real-time voice and text chat platform.",
    points: [
      "Led the migration of a Laravel (PHP) monolith to Go: analysed the legacy codebase end to end and rebuilt it on Gin, GORM, Redis and JWT, covering 123 API routes and 13 data models.",
      "Designed the real-time messaging backend, REST and gRPC service interfaces, and the relational schema behind chat, users and media.",
      "Resolved MySQL access and GORM AutoMigrate failures during migration, keeping data consistent across the cut-over.",
      "Integrated Cloudflare Turnstile CAPTCHA into the Go auth flow to block bot sign-ups and abuse.",
      "Built a Makefile-driven CI/CD pipeline that cross-compiles Go binaries and ships them to a Hetzner VPS under systemd.",
      "Provisioned and hardened the production API server: SSH key management, access control and service supervision.",
      "Fixed media-compatibility issues by converting WebM uploads to MP4 with FFmpeg, correcting frame-rate and codec mismatches.",
    ],
  },
  {
    role: "Senior Golang Developer",
    company: "GridShip LLC",
    place: "Illinois, USA",
    period: "Remote · From Nov 2023",
    intro: "Led backend architecture and technical direction for a high-volume, AI-assisted SMS & MMS marketing platform.",
    points: [
      "Designed the complete backend architecture for throughput, uptime and clean data flow between services.",
      "Built gRPC microservices in Go for fast, typed service-to-service communication.",
      "Implemented event-driven messaging with Kafka and RabbitMQ for reliable, decoupled delivery pipelines.",
      "Integrated Twilio for SMS/MMS delivery and built Go cron workers for scheduled sends and maintenance.",
      "Designed SQL and NoSQL schemas for high-throughput message and campaign data.",
      "Owned CI/CD and deployments on DigitalOcean; guided the team through code reviews and architecture decisions.",
    ],
  },
  {
    role: "Software Engineer / Team Lead",
    company: "CapRegSoft Pvt. Ltd.",
    place: "Pakistan",
    period: "Jun 2020 – Jun 2023",
    points: [
      "Led a team of full-stack developers in Go and React across web and mobile products; ran code reviews and set engineering practices.",
      "Built scalable backends with REST, GraphQL and WebSockets (Socket.IO), plus serverless workloads on AWS Lambda to cut hosting costs.",
      "Designed relational and NoSQL data models for high-availability systems.",
      "Managed deployment and scaling on AWS (S3, RDS, Cognito), Linode and Namecheap with Docker-based builds and CI/CD.",
      "Partnered with frontend, QA and product teams to deliver features end to end on schedule.",
    ],
  },
  {
    role: "Lab Instructor / Teaching Assistant",
    company: "PAF-IAST, School of Computing Sciences",
    place: "Haripur, Pakistan",
    period: "Part-time",
    points: [
      "Instructor for the Parallel and Distributed Computing lab (COMP-460L), two sections; co-authored lab manual v2.0.",
      "Five semesters as teaching assistant across Programming Fundamentals, OOP, Data Structures and Databases.",
      "Faculty Lead of the Usher Society, School of Computing Sciences.",
    ],
  },
];

export const projects = [
  {
    title: "NimTalk",
    subtitle: "Real-time Voice & Text Chat Platform",
    org: "NimTalk LLC · California, USA",
    tech: ["Go", "Gin", "GORM", "MySQL", "Redis", "JWT", "Hetzner"],
    description: "Full Laravel-to-Go rewrite of a live chat product: 123 routes, 13 models, CAPTCHA-protected auth and automated deployment.",
  },
  {
    title: "AI-Powered SMS & MMS Marketing",
    subtitle: "High-volume campaign platform",
    org: "GridShip LLC · Illinois, USA",
    tech: ["Go", "gRPC", "Kafka", "RabbitMQ", "Twilio", "Docker"],
    description: "Event-driven microservices for bulk SMS/MMS campaigns with scheduled delivery workers and CI/CD releases.",
  },
  {
    title: "Findr-LotVector",
    subtitle: "Dealership management backend",
    org: "CapRegSoft Pvt. Ltd. (client project)",
    tech: ["Go", "PostgreSQL", "AWS Lambda", "Docker"],
    description: "Management backend for a web and mobile app, built serverless on AWS to scale to millions of users.",
  },
  {
    title: "Mailavail",
    subtitle: "Email Verification",
    org: "CapRegSoft Pvt. Ltd. (client project)",
    tech: ["Go", "MongoDB", "MySQL", "WebSockets"],
    description: "Validates business and personal emails; schema, APIs, queue consumers and live socket updates.",
  },
  {
    title: "Hawnal",
    subtitle: "Go Geo-data Package",
    org: "CapRegSoft Pvt. Ltd.",
    tech: ["Go"],
    description: "Worldwide countries, states and cities data with filtering, e.g. cities by country.",
  },
  {
    title: "CapRegSoft Portal & Website",
    subtitle: "Organizational management system",
    org: "CapRegSoft Pvt. Ltd.",
    tech: ["Go", "AWS S3 / RDS", "Jibble API"],
    description: "Real-time company CMS with attendance tracking, leave requests and salary management.",
  },
];

export const education = {
  degree: "BS Computer Science",
  school: "Pak-Austria Fachhochschule Institute of Applied Sciences and Technology (PAF-IAST), Haripur",
  extra: [
    "Blockchain / Web3 certification assessment — consensus, smart contracts, token standards, DeFi.",
    "Languages: English (professional), Urdu (native).",
  ],
};
